import React, { useEffect, useRef } from 'react';
import { Brain, ShieldCheck, Eye, Sparkles, TrendingUp, AlertTriangle } from 'lucide-react';

const CapabilitiesSection = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const scrolled = window.scrollY;
      const rate = scrolled * 0.2;
      sectionRef.current.style.transform = `translateY(${rate}px)`;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const capabilities = [
    {
      title: "Predictive Analytics",
      description: "AI-native forecasting to anticipate disruptions before they impact your operations.",
      icon: Brain,
      gradient: "from-blue-500 to-cyan-400",
      features: ["98% accuracy", "Real-time updates", "ML-driven"],
      glowColor: "blue"
    },
    {
      title: "Risk Mitigation",
      description: "Identify and address supply chain vulnerabilities with real-time monitoring.",
      icon: ShieldCheck,
      gradient: "from-emerald-500 to-teal-400",
      features: ["Auto-alerts", "Impact scoring", "Recovery plans"],
      glowColor: "emerald"
    },
    {
      title: "Real-Time Visibility",
      description: "Track shipments, inventory, and orders across your entire supply network.",
      icon: Eye,
      gradient: "from-purple-500 to-pink-400",
      features: ["Live tracking", "End-to-end", "IoT integration"],
      glowColor: "purple"
    }
  ];

  const getGlowClass = (color) => {
    switch(color) {
      case 'blue': return 'shadow-blue-500/20 group-hover:shadow-blue-500/40';
      case 'emerald': return 'shadow-emerald-500/20 group-hover:shadow-emerald-500/40';
      case 'purple': return 'shadow-purple-500/20 group-hover:shadow-purple-500/40';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen text-gray-700 relative">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Grid Pattern Overlay */}

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <div className="text-center mb-10 ">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 mb-6">
            <Sparkles className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-medium text-gray-800 tracking-wide">AI-Powered Innovation</span>
          </div>
          
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-6">
            Universal Capabilities
          </h1>
          
          <p className="text-sm md:text-base text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Regardless of your industry, KGS Technology Group provides powerful capabilities that transform 
            how you manage your supply chain.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {capabilities.map((cap, index) => {
            const IconComponent = cap.icon;
            return (
              <div
                key={index}
                className="group relative transform transition-all duration-500 hover:-translate-y-2"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Glowing Border Effect */}
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${cap.gradient} rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition duration-500 ${getGlowClass(cap.glowColor)}`}></div>
                
                {/* Card Content */}
                <div className="relative bg-gradient-to-br from-gray-800/50 to-gray-900/70 backdrop-blur-sm rounded-2xl border border-white/10 p-8 h-full transition-all duration-300 group-hover:border-white/20">
                  
                  {/* Icon Container with Animation */}
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                    <div className={`relative w-16 h-16 bg-gradient-to-br ${cap.gradient} rounded-xl flex items-center justify-center transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6`}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-white mb-4 tracking-tight">
                    {cap.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-gray-300 leading-relaxed mb-6">
                    {cap.description}
                  </p>

                  {/* Feature Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {cap.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10 text-gray-300"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* Learn More Link */}
                  {/* <div className="flex items-center gap-2 text-sm font-medium text-gray-400 group-hover:text-white transition-colors duration-300 cursor-pointer">
                    <span>Explore capability</span>
                    <TrendingUp className="w-4 h-4 transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                  </div> */}

                  {/* Decorative Corner */}
                  <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-white/5 rounded-tr-xl group-hover:border-white/20 transition-all duration-300"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Stats Section */}
        <div className="mt-24 pt-12 border-t border-white/10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: "99.9%", label: "Uptime SLA", icon: "⚡" },
              { value: "<100ms", label: "Response Time", icon: "🚀" },
              { value: "50M+", label: "Transactions", icon: "📊" },
              { value: "24/7", label: "Support", icon: "🎯" }
            ].map((stat, idx) => (
              <div key={idx} className="text-center group">
                <div className="text-4xl mb-2 transform group-hover:scale-110 transition-transform duration-300">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-800 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default CapabilitiesSection;